import { Injectable } from '@angular/core';
import { asapScheduler, asyncScheduler, BehaviorSubject, catchError, filter, map, mergeMap, Observable, observeOn, scan, Subject, throttleTime, throwError } from 'rxjs';
import { Upload } from '../models/ui/upload.model';
import { Collection } from '../models/ui/collection.model';
import { UploadBatchesSdk } from '../sdks/upload-batches.sdk';
import { FileSdk } from '../sdks/file.sdk';
import { tap } from 'rxjs/operators';
import { UploadInstructionsEngine } from '../engines/upload-instructions.engine';
import { UploadProgress } from '../models/ui/upload-progress.model';
import { InsightsUtility } from '../utilities/insights/insights.utility';
import { cloneDeep } from 'lodash-es';
import { S3FileUploadError } from '../insights/errors/s3-file-upload.error';

@Injectable({
	providedIn: 'root'
})
export class UploadManager {
	public uploadProgress$: Observable<UploadProgress>;
	public uploadBatchCollection$: Observable<Collection<Upload>>;

	private uploadBatchCollectionSource: BehaviorSubject<Collection<Upload>> = new BehaviorSubject<Collection<Upload>>(null);
	private pendingSource = new Subject<Observable<any>>();

	constructor(
		private uploadBatchesSdk: UploadBatchesSdk,
		private uploadInstructionsEngine: UploadInstructionsEngine,
		private fileSdk: FileSdk,
		private insightsUtility: InsightsUtility
	) {
		this.uploadBatchCollection$ = this.uploadBatchCollectionSource.asObservable();
		this.subscribeToUploads();
		this.subscribeToUploadProgress();
	}

	public refreshUploadBatchCollection(params?: RefreshParams): void {
		this.uploadBatchesSdk.get({
			skip: params?.skip,
			take: params?.take,
			orderByDirection: params?.orderByDirection,
			orderByColumn: params?.orderByColumn
		}).subscribe((response) => this.uploadBatchCollectionSource.next(response));
	}

	public getUpload(id: string): Observable<Upload> {
		return this.uploadBatchesSdk.get({ id });
	}

	public updateUpload(upload: Upload): Observable<Upload> {
		return this.uploadBatchesSdk.put({ upload });
	}

	public createUpload(upload: Upload): Observable<Upload> {
		return this.uploadBatchesSdk.post({ upload });
	}

	public deleteUploads(ids: string[]): Observable<void> {
		return this.uploadBatchesSdk.delete({ ids });
	}

	public uploadFiles(files: DataTransferItemList | FileList, uploadId: string, isDatUpload: boolean = false): Observable<void> {
		if (this.uploadInstructionsEngine.internalUploadProgress.status !== 'uploading') {
			this.uploadInstructionsEngine.internalUploadProgress.status = 'created';
			this.uploadInstructionsEngine.internalUploadProgress.uploadId = uploadId;
			this.uploadInstructionsEngine.internalUploadProgressSource.next({ ...this.uploadInstructionsEngine.internalUploadProgress });
		}
		return this.uploadInstructionsEngine.createUploadInstructions(files, uploadId, isDatUpload).pipe(
			map((response) => {
				this.uploadInstructionsEngine.internalUploadProgress.totalBytes = this.uploadInstructionsEngine.internalUploadProgress.totalBytes + response.totalBytes;
				this.uploadInstructionsEngine.internalUploadProgress.totalItems = this.uploadInstructionsEngine.internalUploadProgress.totalItems + response.totalItems;
				response.items.forEach((item) => {
					this.pendingSource.next(item);
				});
				this.uploadInstructionsEngine.internalUploadProgressSource.next({ ...this.uploadInstructionsEngine.internalUploadProgress });

				setTimeout(() => {
					this.uploadInstructionsEngine.internalUploadProgress.pendingItemsToAdd =
						this.uploadInstructionsEngine.internalUploadProgress.pendingItemsToAdd - response.items.length;
					this.uploadInstructionsEngine.internalUploadProgressSource.next({ ...this.uploadInstructionsEngine.internalUploadProgress });
				}, 2000);
			}),
			catchError((err) => {
				if (this.uploadInstructionsEngine.internalUploadProgress.status === 'created') {
					this.uploadInstructionsEngine.internalUploadProgress.status = 'inactive';
					this.uploadInstructionsEngine.internalUploadProgressSource.next({ ...this.uploadInstructionsEngine.internalUploadProgress });
				}
				return throwError(err);
			})
		);
	}

	private subscribeToUploads(): void {
		this.pendingSource.pipe(
			tap(() => {
				this.uploadInstructionsEngine.internalUploadProgress.status = 'uploading';
				this.uploadInstructionsEngine.internalUploadProgressSource.next({ ...this.uploadInstructionsEngine.internalUploadProgress });

			}),
			observeOn(asyncScheduler),
			mergeMap((request) => {
				return request;
			}, 5),
			scan((acc, value) => {
				this.uploadInstructionsEngine.internalUploadProgress.completedItems++;
				if (typeof value.partETags === 'undefined') {
					this.uploadInstructionsEngine.internalUploadProgress.completedBytes = this.uploadInstructionsEngine.internalUploadProgress.completedBytes + value.fileSize;
				}
				this.uploadInstructionsEngine.internalUploadProgress.percent = this.uploadInstructionsEngine.computeDownloadPercent();
				this.uploadInstructionsEngine.internalUploadProgressSource.next({ ...this.uploadInstructionsEngine.internalUploadProgress });
				if (acc.length > 30) {
					acc = [];
				}
				acc.push(value);
				return acc;
			}, []),
			filter((items) => items.length > 30 || this.uploadInstructionsEngine.internalUploadProgress.completedItems === this.uploadInstructionsEngine.internalUploadProgress.totalItems),
			mergeMap((items) => {
				const itemsToProcess = [...items];
				items.length = 0;
				return this.fileSdk.completeUploads(itemsToProcess);
			}),
			tap(() => {
				if (this.uploadInstructionsEngine.internalUploadProgress.totalItems === this.uploadInstructionsEngine.internalUploadProgress.completedItems) {
					this.uploadInstructionsEngine.internalUploadProgress.status = 'complete';
					this.uploadInstructionsEngine.internalUploadProgressSource.next({ ...this.uploadInstructionsEngine.internalUploadProgress });
				}
			})
		).subscribe({
			error: (e) => {
				this.insightsUtility.event(new S3FileUploadError(e, e.message, cloneDeep(this.uploadInstructionsEngine.internalUploadProgress)));
			}
		});
	}

	private subscribeToUploadProgress(): void {
		this.uploadProgress$ = this.uploadInstructionsEngine.internalUploadProgressSource.pipe(
			throttleTime(100, asapScheduler, { leading: true, trailing: true })
		);
	}

}

interface RefreshParams {
	skip: number;
	take: number;
	orderByColumn?: string;
	orderByDirection?: 'asc' | 'desc';
}
